import { useAuthorizedQuery } from "../api/authorized-query";
import { seedgreenAPI } from "../api/axios";
import { LotDTO } from "../dtos/LotDTO";
import { STALE_TIME_MEDIUM } from "../queries/query.constants";
import { useQuery, useQueryClient, useMutation } from "@tanstack/vue-query";
import _ from "lodash";
import { computed } from "vue";
const LOT_STALE_TIME = STALE_TIME_MEDIUM;
export function useLots() {
    const query = useQuery({
        enabled: useAuthorizedQuery(),
        queryKey: ["lots"],
        queryFn: async () => (await seedgreenAPI.get("v2/Lot")).data,
        staleTime: LOT_STALE_TIME,
        select: (data) => {
            try {
                return (data?.map((lot) => new LotDTO(lot)) ?? []);
            }
            catch (error) {
                console.error("query/lots select ERROR", error);
            }
        },
    });
    return {
        lots: query.data,
        lotsById: computed(() => _.keyBy(query.data.value, "id")),
        ...query,
    };
}
export function useLot(id) {
    const queryClient = useQueryClient();
    const query = useQuery({
        enabled: useAuthorizedQuery(),
        queryKey: ["lot", id],
        queryFn: async () => {
            if (!id.value) {
                return null;
            }
            return (await seedgreenAPI.get(`v2/Lot/${id.value}`)).data;
        },
        staleTime: LOT_STALE_TIME,
        select: (data) => {
            try {
                return new LotDTO(data);
            }
            catch (error) {
                console.error("query/lot select ERROR", error);
            }
        },
        initialData: () => {
            // Search through the existing "lot" queries, and look to see if we
            // already have this lot loaded in one of them
            const queries = queryClient.getQueriesData({ queryKey: ["lots"] });
            for (const [_queryKey, data] of queries) {
                if (Array.isArray(data)) {
                    const existingData = data.find((l) => l.id === id.value);
                    if (existingData)
                        return existingData;
                }
            }
        },
    });
    return {
        lot: query.data,
        ...query,
    };
}
/**
 * Mutation to create a new lot
 * Returns the ID of the newly created lot
 */
export function useCreateLot() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async (data) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const response = await seedgreenAPI.post("v2/Lot", data);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate all lot lists to refresh data
            queryClient.invalidateQueries({ queryKey: ["lots"] });
        },
    });
    return {
        createLot: mutateAsync,
        isPending,
        error,
    };
}
/**
 * Mutation to update an existing lot
 */
export function useUpdateLot() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async ({ id, data }) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            await seedgreenAPI.put(`v2/Lot/${id}`, data);
        },
        onSuccess: (_, { id }) => {
            // Invalidate the specific lot
            queryClient.invalidateQueries({ queryKey: ["lot", id] });
            // Invalidate all lot lists to refresh data
            queryClient.invalidateQueries({ queryKey: ["lots"] });
        },
    });
    return {
        updateLot: mutateAsync,
        isPending,
        error,
    };
}
